import React from 'react';
import {
  Box, List, ListItem, Typography, useTheme,
} from '@mui/material';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../store/hooks';
import { PartnerEnrollmentStatus } from './enums';
import { getPartnerEnrollmentIdentity, getPartnerEnrollmentStatus } from './utils/PartnerEnrollmentUtils';
import {
  selectAllActivePartners,
  selectGetCompanyPartnerRelations,
  selectRecentRebatePartners,
} from '../store/slices/dashboardSlice';
import { IconBox } from './IconBox';
import { ListViewButton } from './ListViewButton';

interface PartnersListProps {
  partnersListId: string;
  maxPartnersShow?: number;
  onBtnClick?: () => void;
  optionalStyles?: React.CSSProperties;
  isServiceNationPaidWithAccess?: boolean;
}

export function PartnersList({
  partnersListId,
  maxPartnersShow,
  onBtnClick,
  optionalStyles = {},
  isServiceNationPaidWithAccess = false,
}: PartnersListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const activePartners = useAppSelector(selectAllActivePartners) || [];
  const companyPartnerRelations = useAppSelector(selectGetCompanyPartnerRelations);
  const recentRebatePartners = useAppSelector(selectRecentRebatePartners) || [];
  const activePartnerIds = activePartners.map((partner) => partner.id);
  const recentRebatePartnerIds = recentRebatePartners.map((partner) => partner.id);

  const PartnerIcon = ({ partnerId }: { partnerId: string }) => {
    const matchedPartner = activePartners.find((partner) => partner.id === partnerId);

    if (matchedPartner) {
      return (
        <IconBox optionalStyles={{ border: `${theme.spacing(0.125)} solid ${theme.palette.grey[300]}` }}>
          <img src={matchedPartner.config?.logoUrl} alt="Logo" style={{ maxWidth: '125%', maxHeight: '125%', objectFit: 'contain' }} />
        </IconBox>
      );
    }

    return (
      <IconBox optionalStyles={{ background: theme.palette.common.black }}>
        <QuestionMarkIcon sx={{ color: theme.palette.common.white, fontSize: 24 }} />
      </IconBox>
    );
  };

  const PartnerRelationsList = () => {
    const filteredPartners = (companyPartnerRelations ?? [])
      .filter((item) => {
        // For SN Paid: show partners with rebates in last 9 months
        if (isServiceNationPaidWithAccess) {
          return activePartnerIds.includes(item.partnerId) &&
                 recentRebatePartnerIds.includes(item.partnerId);
        }
        // For regular users: show completed enrollments
        return activePartnerIds.includes(item.partnerId) &&
               getPartnerEnrollmentStatus(item) === PartnerEnrollmentStatus.Completed;
      })
      .sort((a, b) => {
        // Use partnerName from companyPartnerRelations for sorting
        const nameA = a.partnerName || '';
        const nameB = b.partnerName || '';
        return nameA.localeCompare(nameB);
      })
      .slice(0, maxPartnersShow || companyPartnerRelations?.length || 0)
      .map((item) => ({
        ...item,
        partnerName: item.partnerName === 'HomeDepot' ? 'Home Depot' : item.partnerName,
      }));

    if (filteredPartners.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.625),
            alignSelf: 'stretch',
          }}
        >
          <ErrorIcon color="error" fontSize="small" />
          <Typography
            variant='body1'
          >
            {isServiceNationPaidWithAccess ?
              t('wallet.noRecentRebates') :
              t('wallet.noPartnersEnrolled')
            }
          </Typography>
        </Box >
      );
    }

    return (
      <>
        {filteredPartners.map((item, index) => (
          <ListItem
            key={index}
            data-testid={`${partnersListId}-item-${index}`}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingLeft: 0,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
              }}>
              {item.partnerId && <PartnerIcon partnerId={item.partnerId} />}
              <Box
                id={`${partnersListId}-item-partners-box-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  data-testid={`${partnersListId}-item-name-${index}`}
                  variant='bodyLgProminent'
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.spacing(1.75),
                    },
                  }}
                >
                  {item.partnerName}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(0.625),
                  }}
                >
                  <CheckIcon color="success" fontSize="small" />
                  <Typography
                    data-testid={`${partnersListId}-item-id-${index}`}
                    variant='bodyMd'
                    color='text.secondary'
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        fontSize: theme.spacing(1.75),
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                      },
                    }}
                  >
                    {`${t('wallet.connected')}${getPartnerEnrollmentIdentity(item)}`.trim()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </ListItem>
        ))}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        padding: theme.spacing(3.125),
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(3.125),
        flex: '1 0 0',
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.grey[200]}`,
        [theme.breakpoints.down('sm')]: { padding: `${theme.spacing(1.875)} ${theme.spacing(1)}` },
        ...optionalStyles,
      }}
    >
      <Typography
        id={'wallet-list-title'}
        variant='h2'
        sx={{
          lineHeight: theme.spacing(3),
          [theme.breakpoints.down('sm')]: {
            fontSize: theme.spacing(1.75),
          },
        }}
      >
        {isServiceNationPaidWithAccess ?
          t('wallet.earnedPartnerRewards') :
          t('wallet.rewardsPartnerEnrollment')
        }
      </Typography>
      <List
        sx={{
          width: '100%',
        }}
      >
        <PartnerRelationsList />
      </List>
      {onBtnClick && <ListViewButton
        onBtnClick={onBtnClick}
        listViewId={partnersListId}
        btnLabel={t('wallet.manageRewards')}
      />}
    </Box>
  );
}
